import React, { useEffect, useState } from "react";
import { X, Shield, Settings2, Check } from "lucide-react";
import axios from "axios";
import { useApp, API } from "../AppContext";
import { PROGERE_LEGAL } from "../config/progere-legal";

const STORAGE_KEY = "progere_consent_v1";
const CONSENT_VERSION = PROGERE_LEGAL.versions.cookies;

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch { return null; }
}

function writeStored(payload) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
}

/**
 * Enregistre le consentement côté serveur (registre Loi 25).
 * Fire-and-forget — n'interrompt jamais l'UX.
 */
async function persistConsent(scopes, event, lang) {
  try {
    await axios.post(`${API}/legal/consents`, {
      event,
      version: CONSENT_VERSION,
      scopes,
      lang,
      user_agent: navigator.userAgent,
      url: window.location.href,
    });
  } catch { /* silencieux — non bloquant */ }
}

/**
 * ProgereConsentManager — Bannière de consentement Loi 25.
 * Apparaît une seule fois au 1er accès. Enregistre les choix dans
 * localStorage + backend (`/api/legal/consents`).
 */
export default function ProgereConsentManager() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    const defaults = {};
    PROGERE_LEGAL.cookieCategories.forEach((c) => { defaults[c.id] = c.required; });
    return defaults;
  });

  useEffect(() => {
    const stored = readStored();
    if (!stored) setVisible(true);
    else setPrefs(stored.scopes || prefs);
  }, []);

  const T = isFr ? {
    title: "Nous respectons votre vie privée",
    body: "Nous utilisons des témoins essentiels au fonctionnement de PROGÈRE et, avec votre consentement, des témoins fonctionnels et analytiques anonymes. Vous pouvez modifier vos choix à tout moment.",
    acceptAll: "Tout accepter",
    rejectAll: "Refuser les non-essentiels",
    customize: "Personnaliser",
    save: "Enregistrer mes choix",
    prefsTitle: "Préférences de témoins",
    prefsSub: "Choisissez les catégories que vous souhaitez autoriser.",
    required: "Obligatoire",
    close: "Fermer",
    learnMore: "En savoir plus",
    versionLbl: "Version",
  } : {
    title: "We value your privacy",
    body: "We use essential cookies to run PROGÈRE and, with your consent, functional and anonymous analytics cookies. You can change your choices at any time.",
    acceptAll: "Accept all",
    rejectAll: "Reject non-essential",
    customize: "Customize",
    save: "Save my choices",
    prefsTitle: "Cookie preferences",
    prefsSub: "Choose the categories you wish to allow.",
    required: "Required",
    close: "Close",
    learnMore: "Learn more",
    versionLbl: "Version",
  };

  const grantAll = async () => {
    const all = {};
    PROGERE_LEGAL.cookieCategories.forEach((c) => { all[c.id] = true; });
    writeStored({ version: CONSENT_VERSION, scopes: all, ts: new Date().toISOString() });
    setPrefs(all); setVisible(false); setShowPrefs(false);
    persistConsent(all, "granted_all", lang);
  };
  const rejectAll = async () => {
    const only = {};
    PROGERE_LEGAL.cookieCategories.forEach((c) => { only[c.id] = !!c.required; });
    writeStored({ version: CONSENT_VERSION, scopes: only, ts: new Date().toISOString() });
    setPrefs(only); setVisible(false); setShowPrefs(false);
    persistConsent(only, "rejected_optional", lang);
  };
  const saveCustom = async () => {
    writeStored({ version: CONSENT_VERSION, scopes: prefs, ts: new Date().toISOString() });
    setVisible(false); setShowPrefs(false);
    persistConsent(prefs, "custom", lang);
  };

  if (!visible && !showPrefs) return null;

  const banner = (
    <div
      data-testid="consent-banner"
      className="fixed left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md bottom-4 z-[70] rounded-2xl p-5 sm:p-6"
      style={{
        background: "var(--pg-bg-card)",
        border: "1px solid var(--pg-border-strong)",
        boxShadow: "var(--pg-shadow)",
        color: "var(--pg-text-primary)",
      }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: "color-mix(in srgb, var(--pg-gold) 14%, transparent)",
            border: "1px solid color-mix(in srgb, var(--pg-gold) 32%, transparent)",
          }}
        >
          <Shield size={18} style={{ color: "var(--pg-gold)" }} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-base" style={{ color: "var(--pg-text-primary)" }}>{T.title}</h3>
          <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--pg-text-secondary)" }}>{T.body}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button
          data-testid="consent-accept-all"
          onClick={grantAll}
          className="text-xs font-semibold px-4 py-2 rounded-full transition"
          style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
        >
          {T.acceptAll}
        </button>
        <button
          data-testid="consent-reject"
          onClick={rejectAll}
          className="text-xs font-semibold px-4 py-2 rounded-full transition"
          style={{ background: "transparent", color: "var(--pg-text-secondary)", border: "1px solid var(--pg-border-strong)" }}
        >
          {T.rejectAll}
        </button>
        <button
          data-testid="consent-customize"
          onClick={() => { setVisible(false); setShowPrefs(true); }}
          className="text-xs font-semibold px-4 py-2 rounded-full transition inline-flex items-center gap-1.5"
          style={{ background: "transparent", color: "var(--pg-text-secondary)", border: "1px solid var(--pg-border)" }}
        >
          <Settings2 size={12} /> {T.customize}
        </button>
      </div>
      <div className="text-[10px] mt-3" style={{ color: "var(--pg-text-muted)" }}>
        {T.versionLbl} {CONSENT_VERSION} · <a href="/temoins" style={{ color: "var(--pg-gold)" }}>{T.learnMore}</a>
      </div>
    </div>
  );

  const prefsModal = (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="consent-prefs-modal">
      <div className="absolute inset-0" style={{ background: "var(--pg-bg-overlay)", backdropFilter: "blur(6px)" }} onClick={() => setShowPrefs(false)} />
      <div
        className="relative rounded-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto p-6 sm:p-8"
        style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border-strong)", boxShadow: "var(--pg-shadow)" }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl font-bold" style={{ color: "var(--pg-text-primary)" }}>{T.prefsTitle}</h2>
            <p className="text-sm mt-1" style={{ color: "var(--pg-text-secondary)" }}>{T.prefsSub}</p>
          </div>
          <button data-testid="consent-close" onClick={() => setShowPrefs(false)} className="p-2 rounded-full hover:bg-black/10" aria-label={T.close}>
            <X size={16} style={{ color: "var(--pg-text-muted)" }} />
          </button>
        </div>

        <div className="space-y-3 mt-4">
          {PROGERE_LEGAL.cookieCategories.map((cat) => (
            <label
              key={cat.id}
              data-testid={`consent-cat-${cat.id}`}
              className="flex items-start gap-3 p-4 rounded-xl cursor-pointer"
              style={{
                background: "var(--pg-bg-tertiary)",
                border: "1px solid var(--pg-border)",
                opacity: cat.required ? 0.85 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={!!prefs[cat.id]}
                disabled={cat.required}
                onChange={(e) => setPrefs((p) => ({ ...p, [cat.id]: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-current"
                style={{ accentColor: "var(--pg-gold)" }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold" style={{ color: "var(--pg-text-primary)" }}>
                    {isFr ? cat.label.fr : cat.label.en}
                  </div>
                  {cat.required && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: "var(--pg-border)", color: "var(--pg-text-muted)" }}>
                      {T.required}
                    </span>
                  )}
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--pg-text-secondary)" }}>
                  {isFr ? cat.description.fr : cat.description.en}
                </p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 mt-6">
          <button
            data-testid="consent-reject-2"
            onClick={rejectAll}
            className="text-xs font-semibold px-4 py-2.5 rounded-full flex-1"
            style={{ background: "transparent", color: "var(--pg-text-secondary)", border: "1px solid var(--pg-border-strong)" }}
          >
            {T.rejectAll}
          </button>
          <button
            data-testid="consent-save"
            onClick={saveCustom}
            className="text-xs font-semibold px-4 py-2.5 rounded-full flex-1 inline-flex items-center justify-center gap-1.5"
            style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
          >
            <Check size={12} /> {T.save}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {visible && banner}
      {showPrefs && prefsModal}
    </>
  );
}

/** Helper : réouvre les préférences depuis un lien "Gérer mes témoins" */
export function openConsentPreferences() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  window.location.reload();
}
